/**
 * Shared React Query options.
 *
 * Defaults are deliberately conservative: monitoring data changes on the scale of
 * minutes, and the workspace must not hammer an API that is still being built.
 */

import type { QueryClient } from '@tanstack/react-query';
import { QueryClient as QueryClientImpl } from '@tanstack/react-query';

import { isApiError } from '../api/errors';

/** List data is considered fresh for a minute; the UI offers a manual refresh. */
export const LIST_STALE_TIME_MS = 60_000;
export const LIST_GC_TIME_MS = 5 * 60_000;

/** At most one retry, and only for failures that can plausibly succeed later. */
export const MAX_QUERY_RETRIES = 1;

/**
 * Retries transient failures only.
 *
 * Retrying a 404 or 501 cannot change the outcome and, worse, keeps the UI in a
 * loading state so an operator waits before being told the endpoint is missing.
 * Definitive answers (missing endpoint, bad credentials, rejected input, no
 * permission) surface immediately.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  if (isApiError(error)) return error.retryable;
  // A non-ApiError should not normally escape the transport; retry once anyway.
  return true;
}

export function createQueryClient(): QueryClient {
  return new QueryClientImpl({
    defaultOptions: {
      queries: {
        staleTime: LIST_STALE_TIME_MS,
        gcTime: LIST_GC_TIME_MS,
        retry: shouldRetryQuery,
        refetchOnWindowFocus: false,
        // Abort in-flight requests when a component unmounts or the tenant changes.
        networkMode: 'online',
      },
      mutations: {
        retry: false,
      },
    },
  });
}
